import { DefaultTheme, css, CustomThemeProps } from 'styled-components';

const defaultTheme = ({
  currentTheme,
  toggleTheme,
  setTheme,
  currentFontsSize,
  toggleFontSize,
  schoolColors,
  setSchoolColors,
}: CustomThemeProps): DefaultTheme => {
  const FONTS_SIZE_JUMP_INDEX = 1; // Caso seja maior que 1, definir maiores tamanhos de fonte além do 13.
  const fontSizeIndex = currentFontsSize === 'normal' ? 0 : FONTS_SIZE_JUMP_INDEX;

  const colors = {
    alert_400: 'var(--color-alert-400)', //   red1 #CF0000, red2 #F24C4C
    alert_600: 'var(--color-alert-600)', // #A01B1B red3 #872221
    alert_100: 'var(--color-alert-100)', // #FFD8D8

    warning_400: 'rgb(236, 148, 36)', // #EC9424 orange1 #ff9800, orange2 #ff9800
    warning_200: 'rgb(255, 210, 152)', // #FFD298
    warning_100: 'rgb(255, 232, 194)', // #FFE8C2

    success_400: 'var(--color-success-400)',

    black: '#000000',
    black80: 'rgba(0, 0, 0, .80)',
    black50: 'rgba(0, 0, 0, .50)',
    black30: 'rgba(0, 0, 0, .30)',
    black20: 'rgba(0, 0, 0, .20)',

    white: 'rgba(255, 255, 255, 1)',
    white80: 'rgba(255, 255, 255, .80)',
    white50: 'rgba(255, 255, 255, .50)',
    white25: 'rgba(255, 255, 255, .25)', // white20, white30

    dark_400: 'var(--color-dark-400)', // #0F0F0F gray4
    dark_350: 'var(--color-dark-350)', // #171918 gray3 #1D1F1E
    dark_350_70: 'var(--color-dark-350-70)',
    dark_150: 'var(--color-dark-150)', // #2A2C2B gray2

    gray_600: 'rgb(66, 69, 67)', // gray1 #424543, gray5 #616663, gray80
    gray_500: 'rgb(142, 143, 142)', // #8E8F8E light4 #969998, gray50
    gray_400: 'var(--color-gray-400)',
    gray_300: 'rgb(217, 218, 217)', // #D9DAD9 light3 #C8CCCA
    gray_200: 'var(--color-gray-200)',
    gray_100: 'rgb(236, 236, 236)', // #ECECEC light1 #F0F4F2

    primary_10: 'rgb(240, 244, 242)', // #F0F4F2

    // School colors

    navbarBg: schoolColors?.navbarBg || '#171918',

    primary_400: schoolColors?.primary_400 || 'rgb(0, 191, 190)', // #00BFBE green3 #00BFBE
    primary_600: schoolColors?.primary_600 || 'rgb(0, 166, 165)', // #00A6A5 green4 #00A6A5
    primary_200: schoolColors?.primary_200 || 'rgb(0, 217, 215)', // #00D9D7 green1 #36F1F0, green2 #00D9D7
    primary_contrast: schoolColors?.primary_contrast || 'rgb(0, 0, 0)',

    secondary_300: schoolColors?.secondary_300 || 'rgb(64, 156, 207)', // #409CCF blue2 #0098D0, blue3 #19A4D7
    secondary_400: schoolColors?.secondary_400 || 'rgb(0, 123, 191)', // #007BBF blue1 #007BBF
    secondary_600: schoolColors?.secondary_600 || 'rgb(0, 111, 172)', // #006FAC blue4 #19A4D7
    secondary_contrast: schoolColors?.secondary_contrast || 'rgb(255, 255, 255)',

    authBackground: 'var(--color-auth-bg)',
    authFloatLinkColor: 'var(--color-auth-float-link)',
    authBoxBackground: 'var(--color-auth-box-bg)',
    authLabelColor: 'var(--color-auth-label)',
    footerColor: 'var(--color-footer)',
  };

  const themes = {
    light: {
      // backgroundPrimary: colors.white,
      // backgroundSecondary: colors.light2,
      // lineContrast: colors.light1,
      // lineContrastSecondary: colors.black30,
      // textColorPrimary: colors.black,
      // textColorSecondary: colors.black50,
      // backgroundContrast: colors.light1,
      color30: colors.black30,
      color50: colors.black50,
      color80: colors.black80,
      color100: colors.black,
      quizQuestionsBg: colors.white,
      quizActiveQuestionBg: colors.gray_100,
      quizSidebarBg: colors.gray_200,
      videoTimelineBg: colors.gray_100,
      videoTimelineActiveBg: colors.white,
      videoTimelineActiveDegrade: colors.gray_300,
      videoContentBg: colors.gray_200,
      articleBg: colors.white,
      articleLine: colors.gray_200,

      commentsBg: colors.gray_200,
      commentsFormBg: colors.white,
      commentsFormBgHover: colors.gray_200,
      commentsLine: colors.gray_500,
      commentsAvatarBg: colors.gray_500,
    },
    dark: {
      // backgroundPrimary: colors.gray2,
      // backgroundSecondary: colors.gray3,
      // lineContrast: colors.white30,
      // lineContrastSecondary: colors.white30,
      // textColorPrimary: colors.white,
      // textColorSecondary: colors.white50,
      // backgroundContrast: colors.gray1,
      color30: colors.white25,
      color50: colors.white50,
      color80: colors.white80,
      color100: colors.white,
      quizQuestionsBg: colors.dark_150,
      quizActiveQuestionBg: colors.gray_600,
      quizSidebarBg: colors.dark_350,
      videoTimelineBg: colors.dark_150,
      videoTimelineActiveBg: colors.dark_350,
      videoTimelineActiveDegrade: colors.dark_350,
      videoContentBg: colors.dark_150,
      articleBg: colors.dark_150,
      articleLine: colors.gray_600,
      commentsBg: colors.dark_350,
      commentsFormBg: colors.gray_600,
      commentsFormBgHover: colors.dark_150,
      commentsLine: colors.gray_600,
      commentsAvatarBg: colors.dark_150,
    },
  };

  const fontSizes = [
    '0.750rem', // 12px - 0
    '0.875rem', // 14px - 1
    '1rem', // 16px - 2
    '1.125rem', // 18px - 3
    '1.313rem', // 21px - 4
    '1.5rem', // 24px - 5
    '2rem', // 32px - 6
    '2.25rem', // 36px - 7
    '2.625rem', // 42px - 8
    '3rem', // 48px - 9
    '3.438rem', // 55px - 10
    '4rem', // 64px - 11
    '4.375rem', // 70px - 12
  ];

  const spacer = 16;
  const space = [
    0, // 0px - 0
    spacer * 0.25, // 4px - 1
    spacer * 0.5, // 8px - 2
    spacer, // 16px - 3
    spacer * 1.5, // 24px - 4
    spacer * 2, // 32px - 5
    spacer * 2.5, // 40px - 6
    spacer * 3, // 48px - 7
    spacer * 3.5, // 56px - 8
    spacer * 4, // 64px - 9,
    spacer * 4.5, // 72px - 10,
    spacer * 5, // 80px - 11
  ];

  const breakpoints = {
    m: '0px',
    t: '640px',
    d: '1024px',
  };

  const mediaQueries = {
    mobile: `@media screen and (min-width: ${breakpoints.m})`,
    tablet: `@media screen and (min-width: ${breakpoints.t})`,
    desktop: `@media screen and (min-width: ${breakpoints.d})`,
  };

  const fonts = {
    normal: 'Roboto, sans-serif',
    slab: '"Roboto Slab", serif',
  };

  const fontWeights = {
    thin: 100,
    light: 300,
    regular: 400,
    medium: 500,
    bold: 700,
  };

  const fontTypes = {
    type0: css`
      font-size: ${fontSizes[0]};
    `,
    type1: css`
      font-size: ${fontSizes[0 + fontSizeIndex]};

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[1 + fontSizeIndex]};
      }
    `,
    type2: css`
      font-size: ${fontSizes[1 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[1 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[2 + fontSizeIndex]};
      }
    `,
    type3: css`
      font-size: ${fontSizes[1 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[2 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[3 + fontSizeIndex]};
      }
    `,
    type4: css`
      font-size: ${fontSizes[2 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[3 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[4 + fontSizeIndex]};
      }
    `,
    type5: css`
      font-size: ${fontSizes[2 + fontSizeIndex]};

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[5 + fontSizeIndex]};
      }
    `,
    type6: css`
      font-size: ${fontSizes[3 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[4 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[6 + fontSizeIndex]};
      }
    `,
    type7: css`
      font-size: ${fontSizes[5 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[6 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[7 + fontSizeIndex]};
      }
    `,
    type8: css`
      font-size: ${fontSizes[5 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[6 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[8 + fontSizeIndex]};
      }
    `,
    type9: css`
      font-size: ${fontSizes[7 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[8 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[9 + fontSizeIndex]};
      }
    `,
    type10: css`
      font-size: ${fontSizes[6 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[8 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[10 + fontSizeIndex]};
      }
    `,
    type11: css`
      font-size: ${fontSizes[8 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[10 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[11 + fontSizeIndex]};
      }
    `,
    type12: css`
      font-size: ${fontSizes[10 + fontSizeIndex]};

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[11 + fontSizeIndex]};
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[12 + fontSizeIndex]};
      }
    `,
  };

  const degrade = {
    primary: css`
      background: ${colors.primary_400};
      background: linear-gradient(180deg, ${colors.primary_400} 0%, ${colors.primary_600} 100%);
    `,
  };

  return {
    colors: {
      ...colors,
      ...themes[currentTheme],
    },
    breakpoints,
    mediaQueries,
    fontSizes,
    fontWeights,
    fontTypes,
    fonts,
    space,
    degrade,
    currentTheme,
    currentFontsSize,
    toggleTheme,
    setTheme,
    toggleFontSize,
    schoolColors,
    setSchoolColors,
  };
};

export default defaultTheme;
